import ModalSizes from "../../../../enums/modalSizes";
import { List } from "semantic-ui-react";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction, type IObservable } from "interfaces";
import { fetchAccountDependencies } from "features/Accounts/state/thunks/accountDependenciesThunk";
import { connect, type ConnectedProps } from "react-redux";
import { useEffect, useState } from "react";
import { resetState } from "features/Accounts/state/slices/accountDependenciesSlice";
import { TriggerableConfirmationModal } from "components";
import "./DeleteAccountModal.scss";
import { Typography } from "components/common/Typography/Typography";

export interface DeleteAccountModalProps extends PropsFromRedux {
  deleteAccountObserver: IObservable<(onConfirm: () => void, id: number, name: string) => void>;
}

const DeleteAccountConfirmationModal: React.FC<DeleteAccountModalProps> = ({
  state,
  accountDependencies,
  reset,
  deleteAccountObserver,
  fetch,
}) => {
  const [accountId, setAccountId] = useState<Nullable<number>>(null);
  useEffect(() => {
    if (accountId) {
      fetch(accountId);
    }

    return reset;
  }, [accountId, reset, fetch]);

  const hasPacks = accountDependencies.hasAssignedPacks;
  const hasChildren = accountDependencies.hasChildAccounts;
  const hasDependencies = hasPacks || hasChildren;

  const getContent = () => {
    if (hasDependencies) {
      return (
        <>
          <Typography variant="title1">
            You cannot delete this account. This could because it has at least one of the following issues:
          </Typography>
          <List bulleted className="delete-account-modal-list">
            {hasChildren && <List.Item>Active child accounts</List.Item>}
            {hasPacks && <List.Item>Published pack(s) available in the Marketplace</List.Item>}
          </List>
          <Typography variant="title1">
            These must be deleted or linked to a different parent account before this account can be deleted.
          </Typography>
        </>
      );
    }

    return (
      <>
        <List.Header className="delete-account-modal-header">
          <Typography bold variant="title1">
            Deleting this account will:
          </Typography>
        </List.Header>
        <List bulleted className="delete-account-modal-list">
          <List.Item>Delete all account data.</List.Item>
          <List.Item>Delete all users and user data.</List.Item>
          <List.Item>Delete any custom content uploaded by this account.</List.Item>
        </List>
        <div className="delete-account-modal-desc">
          <Typography bold variant="title1" color="red">
            This action cannot be undone
          </Typography>
        </div>
      </>
    );
  };

  const isLoading = state !== "fetched";

  return (
    <TriggerableConfirmationModal
      size={hasDependencies ? ModalSizes.tiny : ModalSizes.small}
      onTriggerModalObserver={deleteAccountObserver}
      title={hasDependencies ? "Unable to Delete Account" : "Delete this account?"}
      confirmButtonLabel={hasDependencies ? null : "Delete"}
      cancelButtonLabel={hasDependencies ? "Close" : undefined}
      onTrigger={(id) => setAccountId(id)}
      onClose={() => setAccountId(null)}
      loading={isLoading}
      disabled={isLoading}
      content={getContent()}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  accountDependencies: state.accounts.accountDependencies,
  state: state.accounts.accountDependencies.state,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetch: bindAction(fetchAccountDependencies, dispatch),
  reset: bindAction(resetState, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(DeleteAccountConfirmationModal);

export default ConnectedComponent;
